import React, { useState, useEffect } from "react";
import { Button, Form, Input, Select, DatePicker, TimePicker, Descriptions, message } from "antd";
import dayjs from "dayjs";
import { putRequest, postRequest, getRequest } from "../service/index";
import { getEmployeeId } from "../utils/localStorage";

const ATTENDANCE_STATUS_OPTIONS = [
  { label: "Present", value: "present" },
  { label: "Late", value: "late" },
  { label: "Early", value: "early" },
  { label: "Late & Early", value: "late & early" }
];

const REQUEST_STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" }
];

const REQUIRED_HOURS = 9; // Required working hours per day

const getStatusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "approved":
      return "success";
    case "pending":
      return "warning";
    case "rejected":
      return "error";
    default:
      return "default";
  }
};

// Calculate working hours and overtime
const calculateHours = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return { workingHours: "", overtime: "" };



  const checkInTime = dayjs(checkIn);
  const checkOutTime = dayjs(checkOut);

  if (!checkInTime.isValid() || !checkOutTime.isValid()) {

    return { workingHours: "", overtime: "" };
  }

  // Handle overnight shifts (end time is earlier than start time)
  let totalHours;
  if (checkOutTime.isBefore(checkInTime)) {
    // This is an overnight shift, add 24 hours to the end time
    const adjustedCheckOut = checkOutTime.add(1, 'day');
    totalHours = adjustedCheckOut.diff(checkInTime, 'hour', true);
  } else {
    // Regular same-day shift
    totalHours = checkOutTime.diff(checkInTime, 'hour', true);
  }


  if (totalHours < 0) {
    return { workingHours: "", overtime: "" };
  }

  // Working hours is the complete hours worked (not capped)
  const workingHours = totalHours.toFixed(2);

  // Overtime is any hours beyond required hours, formatted as HH:mm
  const overtimeHours = Math.max(0, totalHours - REQUIRED_HOURS);
  const overtimeHoursInt = Math.floor(overtimeHours);
  const overtimeMinutes = Math.round((overtimeHours - overtimeHoursInt) * 60);

  // Format overtime as HH:mm
  const overtime = overtimeHours > 0
    ? `${overtimeHoursInt.toString().padStart(2, '0')}:${overtimeMinutes.toString().padStart(2, '0')}`
    : "";

  const result = {
    workingHours: workingHours,
    overtime: overtime
  };

  return result;
};



const AttendanceLogRequest = ({ onClose, requestData, onSuccess }) => {
  // Early return check must come before any hooks
  if (!requestData) {
    return (
      <div className="p-4">
        <p>No request data available</p>
        <Button onClick={onClose}>Close</Button>
      </div>
    );
  }
 
  const [employeeList, setEmployeeList] = useState();
  const [form] = Form.useForm();
  const [currentStatus, setCurrentStatus] = useState(requestData?.status || "pending");
  const [loading, setLoading] = useState(false);
  const approvedBy = getEmployeeId();
  const [myEmployee, setMyEmployee] = useState(null);

  const initialValues = {
    date: requestData.requestEditShiftDate
      ? dayjs(requestData.requestEditShiftDate)
      : requestData.attendanceLog?.date
        ? dayjs(requestData.attendanceLog.date)
        : null,
    checkIn: requestData.attendanceLog?.checkIn &&
      requestData.attendanceLog?.checkIn !== "-" &&
      requestData.attendanceLog?.checkIn !== "ss"
      ? dayjs(requestData.attendanceLog.checkIn, "HH:mm")
      : requestData.changeStartTime
        ? dayjs(requestData.changeStartTime, "HH:mm")
        : null,
    checkOut: requestData.attendanceLog?.checkOut &&
      requestData.attendanceLog?.checkOut !== "-" &&
      requestData.attendanceLog?.checkOut !== "ss"
      ? dayjs(requestData.attendanceLog.checkOut, "HH:mm")
      : requestData.changeEndTime
        ? dayjs(requestData.changeEndTime, "HH:mm")
        : null,
    workingHour: requestData.attendanceLog?.workingHour || "",
    overtime: requestData.attendanceLog?.overtime || "",
    attendanceStatus: requestData.attendanceLog?.status
      ? requestData.attendanceLog.status.toLowerCase()
      : "",
    status: requestData.status || "pending",
    response: requestData.response || "",
  };
  useEffect(() => {
    const fetchAttendanceLog = async () => {
      const response = await getRequest(`/api/employees/${requestData.employeeId}`);
      setMyEmployee(response.employee);
    };
    const fetchEmployeeList = async () => {
      try {
        const response = await getRequest(`/api/companies/teammembers?employeeId=${requestData.employeeId}`);
        if (response?.employee) {
          setEmployeeList(response);
        }
      } catch (error) {
        console.error('Error fetching employee data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchEmployeeList();

    fetchAttendanceLog();
  }, []);
  // Set initial values on form mount
  React.useEffect(() => {
    form.setFieldsValue(initialValues);

    // Calculate initial working hours and overtime if check in/out times exist
    if (initialValues.checkIn && initialValues.checkOut) {
      const { workingHours, overtime } = calculateHours(initialValues.checkIn, initialValues.checkOut);
      form.setFieldValue("workingHour", workingHours);
      form.setFieldValue("overtime", overtime);
    }
  }, [requestData, form]); // Added form to dependencies

  // Employee info
  const employee = requestData.employee || {};
  const employeeName = [employee.firstName, employee.lastName].filter(Boolean).join(" ");

  // Check if status is approved
  const isApproved = currentStatus === "approved";

  // Handle status change
  const handleStatusChange = (value) => {
    setCurrentStatus(value);
    form.setFieldValue("status", value);
  };

  // Handle update request
  const handleUpdate = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      const payload = {
        status: values.status,
        approvedBy: approvedBy,
      };

      const response = await putRequest(
        `/api/requestshift?id=${requestData._id}`,
        payload
      );
      const managementIds = (employeeList?.managementChain || []).map(m => m?._id).filter(Boolean);
      const uniqueReceiverIds = Array.from(new Set([employeeList?.employee._id, ...managementIds].filter(Boolean)));
      const res = await postRequest('/api/notifications', {
        "title": `${employeeList.employee.name} Request Shift ${values.status}`,
        "senderId": approvedBy,
        "receiverIds": uniqueReceiverIds,
        "type": "RequestShift",
        "relatedTypeId": response.requestShift._id,
        "relatedDocumentModel": "RequestShift",
        "companyId": myEmployee?.companyId,
        "priority": "medium"
      });
      if (!response.error) {
        // If status is approved, also update the attendance record
        if (values.status === "approved") {
          const { workingHours, overtime } = calculateHours(values.checkIn, values.checkOut);
          const attendancePayloadUpdate = {
            checkIn: values.checkIn ? values.checkIn.format("HH:mm:ss") : "",
            checkOut: values.checkOut ? values.checkOut.format("HH:mm:ss") : "",
            status: values.attendanceStatus,
            workingHour: workingHours,
            overtime: overtime,
            uid: myEmployee.machineCode
          };
          // Compute working hours, required hours, and overtime for create payload
          const checkInTimeCalc = values.checkIn ? dayjs(values.checkIn) : null;
          const checkOutTimeCalc = values.checkOut ? dayjs(values.checkOut) : null;
          let totalHoursWorked = 0;
          if (checkInTimeCalc && checkOutTimeCalc) {
            const adjustedCheckOut = checkOutTimeCalc.isBefore(checkInTimeCalc)
              ? checkOutTimeCalc.add(1, 'day')
              : checkOutTimeCalc;
            totalHoursWorked = adjustedCheckOut.diff(checkInTimeCalc, 'hour', true);
          }
          const workingHourVal = Number.isFinite(totalHoursWorked) ? Number(totalHoursWorked.toFixed(2)) : 0;
          const reqHourVal = Number.parseFloat(myEmployee?.shift?.workingHours ?? 0) || 0;
          const overtimeVal = Math.max(0, workingHourVal - reqHourVal);

          const attendancePayload = {
            employeeId: requestData.employee?._id ,
            machineId: myEmployee.machineCode ,
            companyId: myEmployee.companyId ,
            checkIn: values.checkIn ? values.checkIn.format("HH:mm:ss") : "",
            checkOut: values.checkOut ? values.checkOut.format("HH:mm:ss") : "",
            status: values.attendanceStatus ,
            perday_salary: myEmployee.perday_salary ,
            date: requestData.requestEditShiftDate ,
            detection:  0,
            uid: myEmployee.machineCode,
            workingHour: workingHourVal,
            reqHour: reqHourVal,
            overtime: Number(overtimeVal.toFixed(2)),
            perDaySelery: myEmployee.perhour_salary
          };
          // Determine the endpoint and method based on whether attendanceLog exists
          const attendanceEndpoint = requestData.attendanceLog
            ? `/api/myattendance/${requestData.attendanceLog._id}`
            : `/api/myattendance`;

          const attendanceResponse = requestData.attendanceLog
            ? await putRequest(attendanceEndpoint, attendancePayloadUpdate)
            : await postRequest(attendanceEndpoint, attendancePayload);

          if (attendanceResponse.error) {
            console.error("Error updating attendance:", attendanceResponse.error);
            message.warning("Request updated but attendance update failed");
          } else {
            await putRequest(`/api/requestshift?id=${requestData._id}`, {
                attendanceLogId: attendanceResponse.attendanceLog._id 
            });
            message.success("Request and attendance updated successfully!");
          }
        } else {
          message.success("Request updated successfully!");
        }
        onSuccess && onSuccess();
        onClose();
      } else {
        message.error(response.error || "Failed to update request");
      }
    } catch (error) {
      if (error.errorFields) {
        // validation error, do nothing
      } else {
        message.error("Failed to update request. Please try again.");
        console.error("Error updating request:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle time changes and calculate hours
  const handleTimeChange = () => {
    const checkIn = form.getFieldValue("checkIn");
    const checkOut = form.getFieldValue("checkOut");


    if (checkIn && checkOut) {
      const { workingHours, overtime } = calculateHours(checkIn, checkOut);
      form.setFieldValue("workingHour", workingHours);
      form.setFieldValue("overtime", overtime);
    } else {
      form.setFieldValue("workingHour", "");
      form.setFieldValue("overtime", "");
    }
  };

  // Show details at the top
  return (
    <>
      <div className="flex flex-col gap-3 max-h-[80vh] overflow-y-auto">
        <Descriptions
          bordered
          size="small"
          column={1}
          className="mb-4"
          labelStyle={{ width: 120 }}
        >
          <Descriptions.Item label="Employee">
            {employeeName || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Reason">
            {requestData.reason || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Current Check In">
            {requestData.changeStartTime || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Current Check Out">
            {requestData.changeEndTime || "-"}
          </Descriptions.Item>
        </Descriptions>
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
        >
          {/* Status field at the top */}
          <div className="bg-gray-50 rounded-lg">
            <Form.Item label="Request Status" name="status" className="mb-0">
              <Select
                options={REQUEST_STATUS_OPTIONS}
                placeholder="Select status"
                allowClear
                onChange={handleStatusChange}
              />
            </Form.Item>
          </div>

          <div className="flex gap-4">
            <Form.Item label="Date" name="date" className="flex-1 mb-0">
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabled={!isApproved}
              />
            </Form.Item>
            <Form.Item label="Check In" name="checkIn" className="flex-1 mb-0">
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                minuteStep={1}
                disabled={!isApproved}
                onChange={handleTimeChange}
              />
            </Form.Item>
          </div>
          {/* Show Check Out and Working Hours in one line */}
          <div className="flex gap-4">
            <Form.Item label="Check Out" name="checkOut" className="flex-1 mb-0">
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                minuteStep={1}
                disabled={!isApproved}
                onChange={handleTimeChange}
              />
            </Form.Item>
            <Form.Item label="Working Hours" name="workingHour" className="flex-1 mb-0">
              <Input disabled />
            </Form.Item>
          </div>
          {/* Show Overtime and Attendance Status in one line */}
          <div className="flex gap-4">
            <Form.Item label="Overtime" name="overtime" className="flex-1 mb-0">
              <Input disabled />
            </Form.Item>
            <Form.Item label="Attendance Status" name="attendanceStatus" className="flex-1 mb-0">
              <Select
                options={ATTENDANCE_STATUS_OPTIONS}
                placeholder="Select status"
                disabled={!isApproved}
                allowClear
              />
            </Form.Item>
          </div>
        </Form>
      </div>
      <div className="flex items-center justify-end gap-3 mt-4">

        <Button onClick={onClose}>
          Close
        </Button>
        <Button
          type="primary"
          className="bg-primary text-white"
          onClick={handleUpdate}
          loading={loading}
        >
          Update
        </Button>
      </div>
    </>
  );
};

export default AttendanceLogRequest;
